<template>
    <div class="tool-selector">
        <div class="search-container mb-2">
            <input type="text" class="w-full p-2 bg-gray-700 text-white rounded-lg" placeholder="Search for tools..."
                v-model="searchTerm" @focus="showDropdown = true" @blur="onBlur" @input="showDropdown = true"
                autocomplete="off" />
        </div>

        <div v-if="showDropdown && filteredTools.length > 0"
            class="tool-results mb-2 max-h-60 overflow-y-auto bg-gray-700 rounded-lg">
            <div v-for="tool in filteredTools" :key="tool" @mousedown.prevent="addTool(tool)"
                class="tool-item p-2 flex items-center hover:bg-gray-600 cursor-pointer">
                <font-awesome-icon :icon="toolIcons[tool] || ['fas', 'code']" :style="{ color: getToolIconColor(tool) }"
                    class="mr-2" />
                <span>{{ tool }}</span>
            </div>
        </div>

        <div class="selected-tools">
            <div class="flex flex-wrap gap-2 bg-gray-700 p-2 rounded-lg">
                <div v-for="tool in selectedTools" :key="tool"
                    class="tool-tag flex items-center bg-gray-600 px-2 py-1 rounded">
                    <font-awesome-icon :icon="toolIcons[tool] || ['fas', 'code']"
                        :style="{ color: getToolIconColor(tool) }" class="mr-2" />
                    <span>{{ tool }}</span>
                    <button @click="removeTool(tool)" class="ml-2 text-gray-400 hover:text-white">
                        <font-awesome-icon :icon="['fas', 'times']" />
                    </button>
                </div>
                <div v-if="selectedTools.length === 0" class="text-gray-400 p-1">
                    No tools selected. Search above to add tools.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { toolIcons, getToolIconColor } from '@/utils/toolIcons';

export default {
    name: 'ToolSelector',
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        allTools: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const searchTerm = ref('');
        const selectedTools = ref([]);
        const isInternalUpdate = ref(false);
        const showDropdown = ref(false);

        // Initialize selectedTools with props.modelValue
        selectedTools.value = [...props.modelValue];

        // Watch for external changes to modelValue
        watch(() => props.modelValue, (newVal) => {
            if (JSON.stringify(newVal) !== JSON.stringify(selectedTools.value)) {
                isInternalUpdate.value = true;
                selectedTools.value = [...newVal];
                isInternalUpdate.value = false;
            }
        }, { deep: true });

        // Watch for internal changes to selectedTools
        watch(selectedTools, (newVal) => {
            if (!isInternalUpdate.value) {
                emit('update:modelValue', [...newVal]);
            }
        }, { deep: true });

        const filteredTools = computed(() => {
            let tools = props.allTools && props.allTools.length > 0
                ? props.allTools
                : Object.keys(toolIcons);

            // Always show all tools if searchTerm is empty and dropdown is open
            if (!searchTerm.value) {
                return tools.filter(tool => !selectedTools.value.includes(tool));
            }
            return tools.filter(tool =>
                tool.toLowerCase().includes(searchTerm.value.toLowerCase()) &&
                !selectedTools.value.includes(tool)
            );
        });

        const addTool = (tool) => {
            if (!selectedTools.value.includes(tool)) {
                selectedTools.value = [...selectedTools.value, tool];
            }
            searchTerm.value = '';
            showDropdown.value = false;
        };

        const removeTool = (tool) => {
            selectedTools.value = selectedTools.value.filter(t => t !== tool);
        };

        const onBlur = () => {
            // Delay to allow click event to register before hiding dropdown
            setTimeout(() => {
                showDropdown.value = false;
            }, 100);
        };

        return {
            searchTerm,
            selectedTools,
            filteredTools,
            addTool,
            removeTool,
            toolIcons,
            getToolIconColor,
            showDropdown,
            onBlur
        };
    }
};
</script>

<style scoped>
.tool-results {
    border: 1px solid #4a5568;
}

.tool-tag {
    transition: all 0.2s ease;
}

.tool-tag:hover {
    background-color: #4a5568;
}
</style>
